import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService, TreeNode } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { AbstractService } from 'src/app/services/abstract.service';
import { RelatorioDinamicoService } from 'src/app/services/relatorio-dinamico.service';

type Situacao = 'TODOS' | 'PUBLICADO' | 'PILOTO' | 'RASCUNHO' | 'ARQUIVADO';

@Component({
  selector: 'habil-relatorios-dinamicos',
  templateUrl: './relatorios-dinamicos.component.html',
  styleUrls: ['./relatorios-dinamicos.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class RelatoriosDinamicosComponent implements OnInit {
  aba: 'modelos' | 'fontes' = 'modelos';
  carregando = false;

  modelos: any[] = [];
  fontes: any[] = [];
  categorias: any[] = [];
  arvore: TreeNode[] = [];
  categoriaSelecionada: TreeNode = null;

  busca = '';
  situacao: Situacao = 'TODOS';
  situacoes: { label: string; value: Situacao }[] = [];

  podeSql = false;
  podeIncluir = false;
  podeEditarCategoria = false;
  podeExcluir = false;

  mostrarCategorias = false;
  categoriaEdicao: any = null;

  mostrarNovoModelo = false;
  fontesModelo: any[] = [];
  novoModelo = { fonte: null as any, chave: '', titulo: '', categoriaId: null as number };
  opcoesCategoria: { label: string; value: number }[] = [];

  constructor(
    private service: RelatorioDinamicoService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    breadcrumbService: BreadcrumbService,
    titleService: Title
  ) {
    breadcrumbService.setItems([{ label: 'Relatórios dinâmicos' }]);
    titleService.setTitle('Relatórios dinâmicos - ' + AbstractService.app);
  }

  ngOnInit(): void {
    this.podeSql = this.service.pode('S');
    this.podeIncluir = this.service.pode('I');
    this.podeEditarCategoria = this.service.pode('E') || this.service.pode('I');
    this.podeExcluir = this.service.pode('D');
    this.carregar();
  }

  carregar(): void {
    this.carregando = true;
    forkJoin({
      modelos: this.service.modelos(),
      categorias: this.service.categorias(),
      fontes: this.podeSql ? this.service.fontes() : of([]),
    })
      .pipe(catchError((e) => {
        this.erro(e);
        return of(null);
      }))
      .subscribe((r) => {
        this.carregando = false;
        if (!r) {
          return;
        }
        this.modelos = r.modelos;
        this.fontes = r.fontes;
        this.categorias = r.categorias;
        this.montarArvore();
        this.contarSituacoes();
      });
  }

  get modelosFiltrados(): any[] {
    const termo = this.busca.trim().toLowerCase();
    const categorias = this.categoriaSelecionada?.data ? this.descendentes(this.categoriaSelecionada.data.id) : null;
    return this.modelos.filter((m) => {
      if (this.situacao === 'TODOS' ? m.situacao === 'ARQUIVADO' : m.situacao !== this.situacao) {
        return false;
      }
      if (categorias && !categorias.has(m.categoriaId)) {
        return false;
      }
      return !termo || m.titulo?.toLowerCase().includes(termo) || m.chave?.toLowerCase().includes(termo);
    });
  }

  get fontesFiltradas(): any[] {
    const termo = this.busca.trim().toLowerCase();
    return this.fontes.filter((f) => !termo || f.titulo?.toLowerCase().includes(termo) || f.chave?.toLowerCase().includes(termo));
  }

  abrirModelo(m: any): void {
    this.router.navigate(['relatorios-dinamicos/modelo'], { queryParams: { id: m.id } });
  }

  excluirModelo(m: any): void {
    const publicado = !!(m.versaoPublicada || m.versaoPiloto);
    this.confirmationService.confirm({
      message: publicado
        ? `Excluir "${m.titulo}" e todas as versões? Ele some dos clientes em até 1 minuto e não dá para desfazer.`
        : `Excluir "${m.titulo}"?`,
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.service.excluirModelo(m.id).subscribe(
          () => {
            this.modelos = this.modelos.filter((x) => x.id !== m.id);
            this.contarSituacoes();
            this.recarregarFontes();
            this.messageService.add({ severity: 'success', summary: 'Modelo excluído', detail: m.titulo });
          },
          (e) => this.erro(e)
        ),
    });
  }

  excluirFonte(f: any): void {
    this.confirmationService.confirm({
      message: f.versaoAtual
        ? `Excluir a fonte "${f.titulo}" e todas as versões? Não dá para desfazer.`
        : `Excluir a fonte "${f.titulo}"?`,
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.service.excluirFonte(f.id).subscribe(
          () => {
            this.fontes = this.fontes.filter((x) => x.id !== f.id);
            this.messageService.add({ severity: 'success', summary: 'Fonte excluída', detail: f.titulo });
          },
          (e) => this.erro(e)
        ),
    });
  }

  abrirFonte(f: any): void {
    this.router.navigate(['relatorios-dinamicos/fonte'], { queryParams: { id: f.id } });
  }

  novaFonte(): void {
    this.service.importacaoPendente = null;
    this.router.navigate(['relatorios-dinamicos/fonte'], { queryParams: { id: 'nova' } });
  }

  criarComIa(): void {
    this.router.navigate(['relatorios-dinamicos/ia']);
  }

  importarJrxml(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (!arquivo) {
      return;
    }
    this.carregando = true;
    this.service.importarJrxml(arquivo).subscribe(
      (importacao) => {
        this.carregando = false;
        this.service.importacaoPendente = importacao;
        this.router.navigate(['relatorios-dinamicos/fonte'], { queryParams: { id: 'nova' } });
      },
      (e) => {
        this.carregando = false;
        this.erro(e);
      }
    );
  }

  abrirNovoModelo(): void {
    this.novoModelo = {
      fonte: null,
      chave: '',
      titulo: '',
      categoriaId: this.categoriaSelecionada?.data?.id || null,
    };
    this.service.fontesParaModelo().subscribe(
      (fontes) => {
        this.fontesModelo = fontes.map((f) => ({ label: `${f.titulo} (${f.chave} v${f.versao})`, value: f }));
        this.mostrarNovoModelo = true;
      },
      (e) => this.erro(e)
    );
  }

  sugerirChave(): void {
    if (!this.novoModelo.chave && this.novoModelo.titulo) {
      this.novoModelo.chave = this.novoModelo.titulo
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  }

  criarModelo(): void {
    const fonte = this.novoModelo.fonte;
    if (!fonte || !this.novoModelo.chave || !this.novoModelo.titulo) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Escolha a fonte e informe título e chave.' });
      return;
    }
    const colunas = (fonte.colunas || []).slice(0, 8).map((c: any) => {
      const coluna: any = { coluna: c.nome };
      if (['moeda', 'decimal'].includes(c.tipo)) {
        coluna.total = 'soma';
      }
      return coluna;
    });
    const variaveis = (fonte.variaveis || []).filter((v: any) => v.nome && v.total && v.coluna).map((v: any) => ({ variavel: v.nome, totalGeral: true }));
    const modelo = {
      versaoSchema: 1,
      chave: this.novoModelo.chave,
      titulo: this.novoModelo.titulo,
      fonte: fonte.chave,
      filtros: (fonte.parametros || []).filter((p: any) => p.tipo !== 'empresas').map((p: any) => ({ parametro: p.nome })),
      colunas,
      agrupamentos: [],
      ordenacao: [],
      indicadores: [],
      ...(variaveis.length ? { variaveis } : {}),
    };
    this.service.criarModelo(modelo, this.novoModelo.categoriaId).subscribe(
      (criado) => {
        this.mostrarNovoModelo = false;
        this.abrirModelo(criado);
      },
      (e) => this.erro(e)
    );
  }

  abrirCategorias(): void {
    this.categoriaEdicao = null;
    this.mostrarCategorias = true;
  }

  editarCategoria(categoria?: any): void {
    this.categoriaEdicao = categoria ? { ...categoria } : { id: null, nome: '', pai: null, ordem: 0, icone: '' };
  }

  salvarCategoria(): void {
    this.service.salvarCategoria(this.categoriaEdicao).subscribe(
      () => {
        this.categoriaEdicao = null;
        this.recarregarCategorias();
      },
      (e) => this.erro(e)
    );
  }

  excluirCategoria(categoria: any): void {
    this.confirmationService.confirm({
      message: `Excluir a categoria "${categoria.nome}"?`,
      accept: () =>
        this.service.excluirCategoria(categoria.id).subscribe(
          () => this.recarregarCategorias(),
          (e) => this.erro(e)
        ),
    });
  }

  categoria(id: number): any {
    return this.categorias.find((c) => c.id === id);
  }

  nomeCategoria(id: number): string {
    return this.categoria(id)?.nome || '';
  }

  classeSituacao(situacao: string): string {
    return 'situacao situacao-' + (situacao || 'RASCUNHO').toLowerCase();
  }

  rotuloSituacao(situacao: string): string {
    switch (situacao) {
      case 'PUBLICADO':
        return 'Publicado';
      case 'PILOTO':
        return 'Piloto';
      case 'ARQUIVADO':
        return 'Arquivado';
      default:
        return 'Rascunho';
    }
  }

  private recarregarFontes(): void {
    if (!this.podeSql) {
      return;
    }
    this.service.fontes().subscribe((f) => (this.fontes = f));
  }

  private recarregarCategorias(): void {
    this.service.categorias().subscribe((c) => {
      this.categorias = c;
      this.montarArvore();
    });
  }

  private montarArvore(): void {
    const porPai = new Map<number, any[]>();
    for (const c of this.categorias) {
      const chave = c.pai || 0;
      porPai.set(chave, [...(porPai.get(chave) || []), c]);
    }
    const montar = (pai: number): TreeNode[] =>
      (porPai.get(pai) || []).map((c) => ({
        label: c.nome,
        data: c,
        expanded: true,
        children: montar(c.id),
      }));
    this.arvore = montar(0);
    this.opcoesCategoria = [];
    const achatar = (nos: TreeNode[], prefixo: string) => {
      for (const n of nos) {
        this.opcoesCategoria.push({ label: prefixo + n.label, value: n.data.id });
        achatar(n.children || [], prefixo + n.label + ' › ');
      }
    };
    achatar(this.arvore, '');
    if (this.categoriaSelecionada?.data) {
      this.categoriaSelecionada = this.acharNo(this.arvore, this.categoriaSelecionada.data.id);
    }
  }

  private acharNo(nos: TreeNode[], id: number): TreeNode {
    for (const n of nos) {
      if (n.data.id === id) {
        return n;
      }
      const filho = this.acharNo(n.children || [], id);
      if (filho) {
        return filho;
      }
    }
    return null;
  }

  private descendentes(id: number): Set<number> {
    const ids = new Set<number>([id]);
    let mudou = true;
    while (mudou) {
      mudou = false;
      for (const c of this.categorias) {
        if (c.pai && ids.has(c.pai) && !ids.has(c.id)) {
          ids.add(c.id);
          mudou = true;
        }
      }
    }
    return ids;
  }

  private contarSituacoes(): void {
    const conta = (s: Situacao) => this.modelos.filter((m) => m.situacao === s).length;
    const ativos = this.modelos.filter((m) => m.situacao !== 'ARQUIVADO').length;
    this.situacoes = [
      { label: `Todos ${ativos}`, value: 'TODOS' },
      { label: `Publicados ${conta('PUBLICADO')}`, value: 'PUBLICADO' },
      { label: `Piloto ${conta('PILOTO')}`, value: 'PILOTO' },
      { label: `Rascunhos ${conta('RASCUNHO')}`, value: 'RASCUNHO' },
      { label: `Arquivados ${conta('ARQUIVADO')}`, value: 'ARQUIVADO' },
    ];
  }

  private erro(e: any): void {
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.service.mensagemErro(e), life: 8000 });
  }
}
